import { getAddressRule } from './index';
import type { AddressFieldValue, AddressRule, AddressSubValue } from './types';

function countryName(code: string): string {
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    return dn.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

export function formatSingleAddress(address: AddressSubValue, rule: AddressRule): string {
  const lines: string[] = [];
  const has = (k: keyof AddressSubValue) => !!address[k];

  switch (rule.country) {
    case 'US':
    case 'CA':
    case 'AU':
    case 'IN':
    case 'BR':
      if (has('line1')) lines.push(address.line1!);
      if (has('line2')) lines.push(address.line2!);
      if (has('city') || has('region') || has('postalCode')) {
        const mid = [address.city, [address.region, address.postalCode].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(', ');
        if (mid) lines.push(mid);
      }
      break;

    case 'JP':
      if (has('postalCode')) lines.push(address.postalCode!);
      if (has('region')) lines.push(address.region!);
      if (has('city')) lines.push(address.city!);
      if (has('line1')) lines.push(address.line1!);
      if (has('line2')) lines.push(address.line2!);
      break;

    case 'GB':
      if (has('line1')) lines.push(address.line1!);
      if (has('line2')) lines.push(address.line2!);
      if (has('city')) lines.push(address.city!);
      if (has('region')) lines.push(address.region!);
      if (has('postalCode')) lines.push(address.postalCode!);
      break;

    default: {
      if (has('line1')) lines.push(address.line1!);
      if (has('line2')) lines.push(address.line2!);
      const locality = [address.postalCode, address.city].filter(Boolean).join(' ');
      if (locality || has('region')) {
        lines.push([locality, address.region].filter(Boolean).join(', '));
      }
      break;
    }
  }

  if (has('country')) lines.push(countryName(address.country!));
  return lines.join('\n');
}

export function formatFieldValue(value: AddressFieldValue): string {
  const primary = value.shipping ?? value.billing;
  if (!primary) return '';
  const primaryRule = getAddressRule(primary.country);
  const primaryText = formatSingleAddress(primary, primaryRule);

  const hasDistinctBilling = !!value.shipping && !!value.billing && !value.sameAsShipping;
  if (!hasDistinctBilling) return primaryText;

  const billingRule = getAddressRule(value.billing!.country);
  const billingText = formatSingleAddress(value.billing!, billingRule);
  return `${primaryText}\n\nBills to:\n${billingText}`;
}
